import Input from "sap/m/Input";
import ComboBox from "sap/m/ComboBox";
import DateRangeSelection from "sap/m/DateRangeSelection";
import Event from "sap/ui/base/Event";
import Menu from "sap/m/table/columnmenu/Menu";
import { FilterArgument } from "./FilterUtils";
import Column from "sap/ui/table/Column";
import List from "../List";
import FilterUtils from "./FilterUtils";
import ODataMetaModel from "sap/ui/model/odata/v4/ODataMetaModel";
import Button from "sap/m/Button";
import HBox from "sap/m/HBox";
import MCDate from "../../formats/Date";

type SourceTypes = Input | ComboBox | DateRangeSelection;

export default class ColumnMenuUtils {

    /**
     * Handles the value change event for column menu sources.
     * It retrieves the source type and applies the appropriate filter to the list based on the column type.
     * The menu is expected to have data attributes for the column, list, and type.
     * @param {Event & { getSource: () => SourceTypes }} event - The event triggered by the source change.
     */
    public static onValueChange(event: Event & { getSource: () => SourceTypes }): void {
        const source : SourceTypes = event.getSource();
        const menu = source.getParent().getParent().getParent() as Menu;
        const type = menu.data("type") as string;
        const column = menu.data("column") as Column;
        if (!column) {
            console.warn("Column is not set in the menu data.");
            return;
        }
        const list = menu.data("list") as List;
        if (!list) {
            console.warn("List is not set in the menu data.");
            return;
        }
        const filters = ColumnMenuUtils.filterArgumentsFromEventType(type, column, source);
        if (filters.length > 0) {
            filters.forEach((filter) => {
                list.removeColumnFilter(column);
                list.addColumnFilter(filter);
            });
            list.applyFilters();
        } else {
            console.warn("No valid filters generated from the event type.");
        }

    }

    /**
     * Filters arguments based on the event type and column.
     * This method generates filter arguments based on the type of the source (Input, ComboBox, DateRangeSelection).
     * It returns an array of FilterArgument objects.
     * @param {string} type - The type of the column (e.g., "string", "boolean", "number", "date").
     * @param {Column} column - The column from which to get the filter property.
     * @param {SourceTypes} source - The source object that triggered the event.
     * @returns {FilterArgument[]} An array of FilterArgument objects.
     */
    private static filterArgumentsFromEventType(type: string, column: Column, source: SourceTypes): FilterArgument[] {
        const params : FilterArgument[] = [];
        switch (type) {
            case "string":
                params.push({
                    column: column.getFilterProperty() || "",
                    operator: "Contains",
                    value1: (source as Input).getValue()
                });
                break;
            case "boolean":
                params.push({
                    column: column.getFilterProperty() || "",
                    operator: "EQ",
                    value1: (source as ComboBox).getSelectedKey() === "true"
                });
                break;
            case "number":
                params.push({
                    column: column.getFilterProperty() || "",
                    operator: "EQ",
                    value1: parseFloat((source as Input).getValue())
                });
                break;
            case "date": {
                const dateRange = source as DateRangeSelection;
                const startDate = dateRange.getDateValue();
                if (!startDate) {
                    console.warn("Start date is not set for date range filtering.");
                    return [];
                }
                const endDate = dateRange.getSecondDateValue();
                if (!endDate) {
                    console.warn("End date is not set for date range filtering.");
                    return [];
                }
                const formatter = new MCDate({
                    pattern: "yyyy-MM-dd",
                });
                params.push({
                    column: column.getFilterProperty() || "",
                    operator: "BT",
                    value1: formatter.formatValue(startDate),
                    value2: formatter.formatValue(endDate)
                });
                break;
            }
            default:
                console.warn(`Unsupported type "${type}" for column menu filtering.`);
        }
        return params;
    }

    /**
     * Loads the column menu for a specific column based on its type.
     * It retrieves the column type from the meta model and loads the appropriate menu fragment.
     * If the meta model or property is not found, it logs a warning.
     * @param {List} list - The list to which the column belongs.
     * @param {Column} column - The column for which to load the menu.
     * @param {string} filterProperty - The property name used for filtering in the column.
     */
    public static loadColumnMenu(list: List, column: Column, filterProperty: string): void {
        const baseBindingPath = list.getListBinding().getPath();
        const metaModel = list.getListBinding().getModel().getMetaModel();
        if (!metaModel || !(metaModel instanceof ODataMetaModel)) {
            console.warn("Meta model is not available for the list binding.");
            return;
        }
        const property = metaModel.getObject(`${baseBindingPath}/${filterProperty}`) as { $Type: string } | undefined;
        if (!property) {
            console.warn(`Property "${filterProperty}" not found in the meta model.`);
            return;
        }
        const columnType = property.$Type;
        if (!columnType) {
            console.warn(`Column type for "${filterProperty}" is not defined in the meta model.`);
            return;
        }
        ColumnMenuUtils.getMenuForColumnType(list, columnType)
            .then((menu) => {
                menu.data("column", column);
                menu.data("list", list);
                column.setShowFilterMenuEntry(false);
                column.setHeaderMenu(menu);
            })
            .catch((error) => {
                console.error(`Error loading menu for column type "${columnType}":`, error);
            });
    }

    /**
     * Retrieves the appropriate menu for a given column type.
     * It loads the menu fragment based on the column type and adds it as a dependent to the list's table.
     * If no specific menu is defined for the column type, it defaults to "string".
     * @param {List} list - The list to which the column belongs.
     * @param {string} columnType - The type of the column (e.g., "Edm.String", "Edm.Boolean", etc.).
     * @returns {Promise<Menu>} A promise that resolves to the loaded Menu instance.
     */
    public static async getMenuForColumnType(list: List, columnType: string): Promise<Menu> {
        let menuName: string;
        switch (columnType) {
            case "Edm.String":
                menuName = "string";
                break;
            case "Edm.Boolean":
                menuName = "boolean";
                break;
            case "Edm.Int32":
            case "Edm.Int64":
            case "Edm.Decimal":
                menuName = "number";
                break;
            case "Edm.Date":
                menuName = "date";
                break;
            default:
                console.warn(`No specific menu defined for column type "${columnType}". Using default.`);
                menuName = "string";
        }
        const menu = await list.getController().loadFragment({
            name: `mc.frontend.view.fragments.columnmenu.${menuName}`,
            addToDependents: true,
        }) as Menu;
        if (!menu) {
            console.error(`Menu for column type "${columnType}" not found.`);
            throw new Error(`Menu for column type "${columnType}" not found.`);
        }
        menu.data("type", menuName);
        return menu;
    }

    /**
     * Clears the filter for a specific column in the list.
     * It retrieves the column and list from the menu data and removes the filter for the column.
     * If the column or list is not set, it logs a warning.
     * @param {Event & { getSource: () => SourceTypes }} event - The event triggered by the clear action.
     */
    public static onClearFilter(event: Event & { getSource: () => Button }): void {
        const button : Button = event.getSource();
        if (!(button.getParent() instanceof HBox)) {
            console.warn("Button parent is not an HBox, cannot clear filter.");
            return;
        }
        const hBox = button.getParent() as HBox;
        if (hBox.getItems().length === 0) {
            console.warn("HBox has no items to clear filter.");
            return;
        }
        const source: SourceTypes = hBox.getItems()[0] as SourceTypes;
        const menu = source.getParent().getParent().getParent() as Menu;
        const column = menu.data("column") as Column;
        const list = menu.data("list") as List;
        if (!column || !list) {
            console.warn("Column or List is not set in the menu data.");
            return;
        }
        if (source instanceof Input) {
            source.setValue("");
        } else if (source instanceof ComboBox) {
            source.setSelectedKey("");
        } else if (source instanceof DateRangeSelection) {
            source.setDateValue(null);
            source.setSecondDateValue(null);
        } else {
            console.warn("Unsupported source type for resetting value.");
        }

        list.removeColumnFilter(column);
        list.applyFilters();
    }
}